import React, { useCallback, useEffect, useState } from 'react';
import FormField from '../common/FormField';
import Alert from '../common/Alert';
import LoadingSpinner from '../common/LoadingSpinner';
import verificationService from '../../services/verificationService';
import { validateRequired } from '../../utils/validators';
import useForm from '../../hooks/useForm';
import './ReferenceResponse.css';

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

const ReferenceResponse = ({ referenceToken }) => {
  const [context, setContext] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      setContext(await verificationService.getReferenceByToken(referenceToken));
    } catch (err) {
      setLoadError(messageFrom(err, 'This reference request could not be loaded. The link may have expired.'));
    }
  }, [referenceToken]);

  useEffect(() => { load(); }, [load]);

  const questionnaireKeys = context ? Object.keys(context.questionnaire || {}) : [];

  const validate = (vals) => {
    const errs = {};
    errs.confirmIdentity = vals.confirmIdentity ? null : 'You must confirm your identity.';
    errs.declaration = vals.declaration ? null : 'You must accept the declaration.';
    questionnaireKeys.forEach((key) => {
      errs[key] = validateRequired(vals[key], 'This field');
    });
    Object.keys(errs).forEach(k => errs[k] == null && delete errs[k]);
    return errs;
  };

  const initialValues = {
    confirmIdentity: false,
    declaration: false,
    ...questionnaireKeys.reduce((acc, key) => ({ ...acc, [key]: '' }), {}),
  };

  const { values, errors, handleChange, handleSubmit } = useForm(initialValues, validate);

  const onSubmit = async (vals) => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const responses = questionnaireKeys.reduce((acc, key) => ({ ...acc, [key]: vals[key] }), {});
      await verificationService.submitReferenceResponse(referenceToken, responses);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(messageFrom(err, 'This reference could not be submitted.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadError) return <Alert type="error" message={loadError} />;
  if (!context) return <LoadingSpinner fullPage />;

  if (submitted) {
    return (
      <div className="reference-response card">
        <div className="card-body text-center">
          <h2 style={{color: 'var(--secondary)'}}>Reference Submitted Successfully</h2>
          <p>Thank you for taking the time to complete this reference.</p>
          <p>You may now close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reference-response card">
      <div className="card-header bg-primary text-white">
        <h2>Reference Request</h2>
      </div>
      <div className="card-body">
        <div className="reference-context">
          <p>You have been asked to provide a <strong>{context.reference_type.toLowerCase()} reference</strong> regarding a rental application.</p>
          <p>Your responses are strictly confidential and will be used solely for the purpose of assessing the applicant's suitability as a tenant.</p>
        </div>

        {submitError && <Alert type="error" message={submitError} />}

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-section">
            <h3>1. Identity Verification</h3>
            <label className="checkbox-label">
              <input type="checkbox" name="confirmIdentity" checked={values.confirmIdentity} onChange={handleChange} />
              <span>
                I confirm that I am <strong>{context.respondent_name}</strong>
                {context.respondent_relationship ? <> ({context.respondent_relationship})</> : null}.
              </span>
            </label>
            {errors.confirmIdentity && <p className="error-text">{errors.confirmIdentity}</p>}
          </div>

          <div className="form-section">
            <h3>2. Questionnaire</h3>
            {questionnaireKeys.map((key) => (
              <FormField
                key={key}
                label={context.questionnaire[key]}
                name={key}
                type="textarea"
                value={values[key]}
                onChange={handleChange}
                error={errors[key]}
                required
              />
            ))}
          </div>

          <div className="form-section">
            <h3>3. Declaration</h3>
            <label className="checkbox-label">
              <input type="checkbox" name="declaration" checked={values.declaration} onChange={handleChange} />
              <span>I declare that the information provided is true and accurate to the best of my knowledge.</span>
            </label>
            {errors.declaration && <p className="error-text">{errors.declaration}</p>}
          </div>

          <div className="reference-actions">
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting…' : 'Submit Reference'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReferenceResponse;
